// dictation.js - Shared voice-dictation helper.
import { AUTH } from './auth.js';
import { CONFIG } from './config.js';
import { UI } from './ui.js';

export const DICTATION = (() => {

  const LANG_KEY        = 'dragons_dictation_lang';
  const REC_MAX_SECONDS = 120;   // safety cap on a single clip

  // Field-hockey vocabulary primes the transcriber so domain words come back
  // spelled correctly. Deliberately NO player names - keeps squad PII out of
  // the transcription request.
  const VOCAB_PROMPT =
    'Field hockey coaching note. Terms: first touch, scanning, press, ' +
    'transition, drag flick, give-and-go, elimination skill, aerial, tackle, ' +
    'interception, deep run, overload, channel, baseline, reverse stick, ' +
    'marking, defensive line, outlet, goalkeeper, clearance, rebound, ' +
    'positioning, communication, work rate.';

  // At most one recording active app-wide.
  // { id, recorder, stream, chunks, timer, seconds, aborted, opts }
  let _active = null;

  function _lang() {
    try { return localStorage.getItem(LANG_KEY) || 'en'; }
    catch { return 'en'; }
  }
  function _setLang(v) {
    try { localStorage.setItem(LANG_KEY, v); } catch { /* ignore */ }
  }

  function _supported() {
    return !!(navigator.mediaDevices &&
              navigator.mediaDevices.getUserMedia &&
              window.MediaRecorder);
  }

  // ── DOM lookups (always fresh - survives caller re-renders) ─────────────────
  function _row(id) {
    const ta = id && document.getElementById(id);
    if (!ta) return null;
    const r = ta.nextElementSibling;
    return (r && r.classList && r.classList.contains('dictation-row')) ? r : null;
  }
  function _micBtn(id) {
    const r = _row(id);
    return r ? r.querySelector('.dictation-mic') : null;
  }
  function _status(id, msg, cls) {
    const r = _row(id);
    if (!r) return;
    const s = r.querySelector('.dictation-status');
    if (s) {
      s.textContent = msg || '';
      s.className = 'dictation-status' + (cls ? ' dictation-status--' + cls : '');
    }
  }

  function _elapsed() {
    const s = _active ? _active.seconds : 0;
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }
  function _updateBtn(id, on) {
    const btn = _micBtn(id);
    if (!btn) return;
    btn.classList.toggle('dictation-mic--on', on);
    btn.textContent = on ? `⏹ Stop · ${_elapsed()}` : '🎙 Record';
  }

  // Attach a mic control below a textarea.
  function attach(textarea, opts = {}) {
    if (!textarea || !textarea.id || !_supported()) return;
    // Don't double-attach to the same element.
    const sib = textarea.nextElementSibling;
    if (sib && sib.classList && sib.classList.contains('dictation-row')) return;

    const recording = !!(_active && _active.id === textarea.id &&
                          _active.recorder && _active.recorder.state === 'recording');
    const lang = _lang();

    const row = document.createElement('div');
    row.className = 'dictation-row';
    row.innerHTML = `
      <button type="button" class="dictation-mic ${recording ? 'dictation-mic--on' : ''}">
        ${recording ? '⏹ Stop' : '🎙 Record'}
      </button>
      <div class="dictation-lang">
        <button type="button" class="dictation-lang-btn ${lang === 'en' ? 'dictation-lang-btn--on' : ''}" data-lang="en">EN</button>
        <button type="button" class="dictation-lang-btn ${lang === 'nl' ? 'dictation-lang-btn--on' : ''}" data-lang="nl">NL</button>
      </div>
      <span class="dictation-status"></span>`;
    textarea.insertAdjacentElement('afterend', row);

    row.querySelector('.dictation-mic')
       .addEventListener('click', () => _toggle(textarea, opts));
    row.querySelectorAll('[data-lang]').forEach(b => {
      b.addEventListener('click', () => {
        _setLang(b.dataset.lang);
        row.querySelectorAll('[data-lang]').forEach(x =>
          x.classList.toggle('dictation-lang-btn--on', x === b));
      });
    });

    // Re-bound mid-recording: restore the live label.
    if (recording) _updateBtn(textarea.id, true);
  }

  async function _toggle(textarea, opts) {
    // Stop an in-progress recording for this field.
    if (_active && _active.recorder && _active.recorder.state === 'recording') {
      _active.recorder.stop();
      return;
    }
    if (_active) return;  // another clip mid-transcription - ignore
    if (!_supported()) { UI.toast('Recording not supported on this device', 'error'); return; }

    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch { _status(textarea.id, 'Microphone permission needed', 'err'); return; }

    let recorder;
    try { recorder = new MediaRecorder(stream); }
    catch {
      stream.getTracks().forEach(t => t.stop());
      UI.toast('Recording not supported on this device', 'error');
      return;
    }

    _active = {
      id: textarea.id, recorder, stream,
      chunks: [], timer: null, seconds: 0, aborted: false, opts: opts || {},
    };

    recorder.ondataavailable = e => {
      if (e.data && e.data.size && _active) _active.chunks.push(e.data);
    };
    recorder.onstop = async () => {
      stream.getTracks().forEach(t => t.stop());
      if (_active && _active.timer) clearInterval(_active.timer);
      if (!_active) return;
      const { id, aborted, opts: o } = _active;
      const blob = new Blob(_active.chunks, { type: recorder.mimeType || 'audio/webm' });
      _updateBtn(id, false);
      if (aborted) { _active = null; return; }
      if (!blob.size) { _status(id, 'No audio captured', 'err'); _active = null; return; }
      await _transcribe(blob, id, o);
      _active = null;
    };

    recorder.start();
    _updateBtn(textarea.id, true);
    _status(textarea.id, 'Recording - tap Stop when done', 'busy');
    _active.timer = setInterval(() => {
      if (!_active) return;
      _active.seconds++;
      _updateBtn(_active.id, true);
      if (_active.seconds >= REC_MAX_SECONDS && _active.recorder.state === 'recording') {
        _active.recorder.stop();
      }
    }, 1000);
  }

  async function _transcribe(blob, id, opts) {
    _status(id, 'Transcribing…', 'busy');
    const btn = _micBtn(id);
    if (btn) btn.disabled = true;
    try {
      const isMp4 = /mp4|mpeg|m4a/.test(blob.type);
      const fd = new FormData();
      fd.append('audio', blob, `clip.${isMp4 ? 'mp4' : 'webm'}`);
      fd.append('language', _lang());
      fd.append('prompt', VOCAB_PROMPT);

      // Transcription rides the exchange-code function (a known-good
      // deployment); it routes audio uploads by their multipart content-type.
      const r = await fetch(`${CONFIG.supabase.url}/functions/v1/exchange-code`, {
        method: 'POST',
        headers: {
          'apikey':        CONFIG.supabase.anonKey,
          'Authorization': `Bearer ${(AUTH.getToken && AUTH.getToken()) || CONFIG.supabase.anonKey}`,
        },
        body: fd,
      });
      const body = await r.json().catch(() => ({}));
      const text = ((body && body.text) || '').trim();
      if (!r.ok || !text) {
        _status(id, 'Could not transcribe - type instead', 'err');
        return;
      }

      const ta = document.getElementById(id);
      if (ta && document.contains(ta)) {
        const cur = ta.value.trim();
        ta.value = cur ? `${cur} ${text}` : text;
        ta.dispatchEvent(new Event('input', { bubbles: true }));
        _status(id, 'Transcribed - check and edit if needed', 'ok');
        if (opts && typeof opts.onText === 'function') opts.onText(ta.value);
      } else if (opts && typeof opts.onOrphan === 'function') {
        opts.onOrphan(text);
      }
    } catch (err) {
      console.error('Dictation transcribe failed:', err);
      _status(id, 'Network error - type instead', 'err');
    } finally {
      const b = _micBtn(id);
      if (b) b.disabled = false;
    }
  }

  /** Stop any in-progress recording without transcribing (e.g. screen change). */
  function cancelActive() {
    if (_active && _active.recorder && _active.recorder.state === 'recording') {
      _active.aborted = true;
      try { _active.recorder.stop(); } catch { /* ignore */ }
    }
  }

  return { attach, cancelActive };
})();
